import { useEffect, useState } from "react";
import moment from "moment-timezone";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  AccountingSystemForCalculatingFinancialStatistics,
  CompareBucket,
  CompareBy,
  MeetingStats,
  OrganizationSubjectAdminResponse,
  StatisticsService,
} from "client/openapi";
import { Dialog, DialogContent } from "components/Dialog";
import PieChart from "components/Statistics/PieChart";
import {
  DAY,
  MEASURES,
  Measure,
  Range,
  Row,
  UNIT,
  alignRows,
  bucketDates,
  changeText,
  format,
  group,
  rangeLabel,
  shows,
  tagOf,
  valueOf,
} from "./data";
import {
  GroupPills,
  MeasurePills,
  StatusBlock,
  SubjectFilter,
  SubjectPills,
} from "./controls";
import {
  ComparisonQuery,
  remember,
  remembered,
  useComparison,
} from "./useComparison";

type Props = Omit<ComparisonQuery, "subjectIds"> & {
  subjects: OrganizationSubjectAdminResponse[];
  zone: string;
};

export default function TimeSeries({ subjects, zone, ...query }: Props) {
  const { orgId, range, compared } = query;
  const [shown, setShown] = useState<Measure[]>(() =>
    remembered(orgId, "chartMeasures", ["revenue", "cost", "net", "hours"])
  );
  const [by, setBy] = useState<CompareBy>(() =>
    remembered(orgId, "chartBy", CompareBy.MONTH)
  );
  const [subjectIds, setSubjectIds] = useState<number[]>([]);
  const { data, failed, loading } = useComparison({ ...query, subjectIds });

  const toggle = (measure: Measure) => {
    const next = shown.includes(measure)
      ? shown.filter((m) => m !== measure)
      : [...shown, measure];
    setShown(next);
    remember(orgId, "chartMeasures", next);
  };
  const regroup = (value: CompareBy) => {
    setBy(value);
    remember(orgId, "chartBy", value);
  };

  const rows = data
    ? alignRows(
        group(data.current.buckets, by),
        data.compared ? group(data.compared.buckets, by) : null
      )
    : [];

  return (
    <div className="space-y-4 bg-blue-50/50 p-6 rounded-lg border border-gray-200 shadow-sm">
      <div className="flex flex-wrap items-center gap-4">
        <MeasurePills shown={shown} onToggle={toggle} />
        <GroupPills by={by} onChange={regroup} />
        <SubjectFilter
          subjects={subjects}
          value={subjectIds}
          onChange={setSubjectIds}
        />
        <div className="ml-auto">
          <SubjectsPie orgId={orgId} range={range} zone={zone} />
        </div>
      </div>
      <SubjectPills
        subjects={subjects}
        value={subjectIds}
        onChange={setSubjectIds}
      />
      {compared && (
        <Legend current={rangeLabel(range)} against={rangeLabel(compared)} />
      )}
      {failed ? (
        <StatusBlock>{failed}</StatusBlock>
      ) : !data ? (
        <StatusBlock>
          <div
            className="spinner-border text-gray-400"
            role="status"
            aria-label="Loading"
          />
          Loading...
        </StatusBlock>
      ) : (
        <div className={loading ? "opacity-60" : ""}>
          <Chart rows={rows} shown={shown} by={by} basis={query.basis} />
        </div>
      )}
    </div>
  );
}

function Legend({ current, against }: { current: string; against: string }) {
  const line = (dashed: boolean) => (
    <svg width="22" height="6" aria-hidden>
      <line
        x1="1"
        y1="3"
        x2="21"
        y2="3"
        stroke="#6B7280"
        strokeWidth="2.5"
        strokeOpacity={dashed ? 0.5 : 1}
        strokeDasharray={dashed ? "5 3" : undefined}
        strokeLinecap="round"
      />
    </svg>
  );
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-600">
      <span className="inline-flex items-center gap-2">
        {line(false)}
        {current}
      </span>
      <span className="inline-flex items-center gap-2">
        {line(true)}
        {against}
      </span>
    </div>
  );
}

function Chart({
  rows,
  shown,
  by,
  basis,
}: {
  rows: Row[];
  shown: Measure[];
  by: CompareBy;
  basis: ComparisonQuery["basis"];
}) {
  const soFar = rows.findIndex(
    (r) => r.current?.started && !r.current.finished
  );
  const first = rows[0]?.current;
  const last = rows[rows.length - 1]?.current;
  const spansYears =
    !!first && !!last && first.start.slice(0, 4) !== last.start.slice(0, 4);
  // On the Meetings basis a later bucket with nothing booked is 0, so the
  // line runs on to a booked one; on Transactions the line stops at today.
  const valueAt = (bucket: CompareBucket | undefined, measure: Measure) =>
    shows(bucket)
      ? valueOf(bucket, measure)
      : bucket &&
        basis === AccountingSystemForCalculatingFinancialStatistics.MEETINGS
      ? 0
      : null;
  const data = rows.map((row) => {
    const point: Record<string, number | null> = { index: row.index };
    MEASURES.forEach(({ value }) => {
      point[value] = valueAt(row.current, value);
      point[`compared_${value}`] = valueAt(row.compared, value);
    });
    return point;
  });
  const money = shown.filter((m) => m !== "hours");
  const hours = shown.includes("hours");
  // Each row is labelled by its place in the current period, counted on from
  // its first day where the current period has already ended (this month so
  // far against all of last month), so the axis never runs back in time.
  const tick = (index: number) => {
    if (!first) return "";
    const offset = index - 1;
    const bucket = rows[offset]?.current;
    const day = bucket
      ? moment(bucket.start, DAY)
      : by === CompareBy.MONTH
      ? moment(first.start, DAY).add(offset, "months")
      : moment(first.start, DAY).add(
          by === CompareBy.WEEK ? offset * 7 : offset,
          "days"
        );
    if (by === CompareBy.MONTH)
      return day.format(spansYears ? "MMM 'YY" : "MMM");
    return day.format("MMM D");
  };
  const moneyTick = (n: number) => {
    const abs = Math.abs(n);
    const sign = n < 0 ? "−" : "";
    return abs >= 1000
      ? `${sign}$${(abs / 1000).toLocaleString("en-US", {
          maximumFractionDigits: 1,
        })}k`
      : `${sign}$${abs.toLocaleString("en-US")}`;
  };
  // A line with one point draws no stroke, so a lone bucket (a month viewed
  // by month) gets a solid dot; the so-far bucket keeps its open one.
  const alone = rows.length === 1;
  const dot =
    (measure: Measure, color: string) =>
    (props: { index: number; cx: number; cy: number | null }) =>
      // A missing value has no height: no dot for it.
      (props.index === soFar || alone) && Number.isFinite(props.cy) ? (
        <circle
          key={`${measure}-${props.index}`}
          cx={props.cx}
          cy={props.cy as number}
          r={4}
          fill={props.index === soFar ? "#fff" : color}
          stroke={color}
          strokeWidth={2}
        />
      ) : (
        <g key={`${measure}-${props.index}`} />
      );
  if (!shown.length) {
    return (
      <StatusBlock>
        Pick Revenue, Cost, Net or Hours to see it here.
      </StatusBlock>
    );
  }
  return (
    <ResponsiveContainer width="100%" height={320}>
      <LineChart
        data={data}
        margin={{ top: 10, right: 16, left: 0, bottom: 0 }}
      >
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="index"
          tickFormatter={tick}
          tick={{ fontSize: 12 }}
          // About a dozen labels, however many buckets.
          interval={Math.max(0, Math.ceil(rows.length / 12) - 1)}
        />
        {money.length > 0 && (
          <YAxis
            yAxisId="money"
            tickFormatter={moneyTick}
            tick={{ fontSize: 12 }}
            width={64}
          />
        )}
        {hours && (
          <YAxis
            yAxisId="hours"
            orientation={money.length ? "right" : "left"}
            tickFormatter={(n: number) => `${n.toLocaleString("en-US")} hrs`}
            tick={{ fontSize: 12 }}
            width={64}
          />
        )}
        <Tooltip
          content={
            <BucketTooltip rows={rows} shown={shown} by={by} basis={basis} />
          }
        />
        {MEASURES.filter((m) => shown.includes(m.value)).flatMap((m) => {
          const axis = m.value === "hours" ? "hours" : "money";
          return [
            <Line
              key={`compared_${m.value}`}
              yAxisId={axis}
              dataKey={`compared_${m.value}`}
              stroke={m.color}
              strokeOpacity={0.45}
              strokeDasharray="6 4"
              strokeWidth={2}
              dot={alone ? { r: 4, fill: m.color, fillOpacity: 0.45 } : false}
              isAnimationActive={false}
            />,
            <Line
              key={m.value}
              yAxisId={axis}
              dataKey={m.value}
              stroke={m.color}
              strokeWidth={2.5}
              dot={dot(m.value, m.color)}
              isAnimationActive={false}
            />,
          ];
        })}
      </LineChart>
    </ResponsiveContainer>
  );
}

function BucketTooltip({
  active,
  label,
  rows,
  shown,
  by,
  basis,
}: {
  active?: boolean;
  label?: number;
  rows: Row[];
  shown: Measure[];
  by: CompareBy;
  basis: ComparisonQuery["basis"];
}) {
  const row = label ? rows[label - 1] : undefined;
  if (!active || !row) return null;
  const { current, compared } = row;
  const tag = tagOf(current, basis);
  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm px-3 py-2 text-sm space-y-1">
      <div className="font-semibold text-slate-800">
        {current ? bucketDates(current, by) : `${UNIT[by]} ${row.index}`}
        {tag && <span className="font-normal text-gray-500"> · {tag}</span>}
      </div>
      {compared && (
        <div className="text-xs text-gray-500">
          compared with {bucketDates(compared, by)}
        </div>
      )}
      {MEASURES.filter((m) => shown.includes(m.value)).map((m) => {
        const now = shows(current) ? valueOf(current, m.value) : null;
        const then = shows(compared) ? valueOf(compared, m.value) : null;
        return (
          <div
            key={m.value}
            className="flex items-center justify-between gap-6"
          >
            <span className="inline-flex items-center gap-2">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: m.color }}
              />
              {m.label}
            </span>
            <span className="text-right">
              <span className="font-semibold text-slate-800">
                {now === null ? "—" : format(m.value, now)}
              </span>
              {then !== null && (
                <span className="text-gray-500">
                  {" "}
                  vs {format(m.value, then)}
                  {now !== null && ` (${changeText(m.value, now, then)})`}
                </span>
              )}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** The period's sessions by subject, in a pop-out. */
function SubjectsPie({
  orgId,
  range,
  zone,
}: {
  orgId: number;
  range: Range;
  zone: string;
}) {
  const [open, setOpen] = useState(false);
  // Undefined while loading, null if it couldn't load.
  const [stats, setStats] = useState<MeetingStats | null>();
  const [hidden, setHidden] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    if (!open) return;
    let live = true;
    setStats(undefined);
    StatisticsService.getOrgMeetingStats({
      orgId,
      start: moment.tz(range[0], zone).startOf("day").toISOString(),
      until: moment.tz(range[1], zone).endOf("day").toISOString(),
      tz: zone,
    })
      .then((result) => live && setStats(result))
      .catch(() => live && setStats(null));
    return () => {
      live = false;
    };
  }, [open, orgId, range, zone]);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="px-4 py-1 rounded-full text-sm font-medium border bg-white border-gray-300 text-gray-700 hover:bg-gray-100"
      >
        Subjects
      </button>
      <DialogContent
        className="dialog-content max-w-[640px]"
        onClose={() => setOpen(false)}
      >
        <h2 className="font-bold text-xl !leading-normal">
          Sessions by subject
        </h2>
        <p className="text-sm text-gray-500 mb-4">{rangeLabel(range)}</p>
        {stats === undefined ? (
          <StatusBlock>Loading...</StatusBlock>
        ) : stats === null ? (
          <StatusBlock>Couldn't load subjects.</StatusBlock>
        ) : stats.summary.length === 0 ? (
          <StatusBlock>No sessions in these dates.</StatusBlock>
        ) : (
          <div className="h-80 flex items-center justify-center">
            <PieChart
              data={stats.summary}
              excluded={hidden}
              onToggle={(name) =>
                setHidden((prev) => {
                  const next = new Set(prev);
                  next.has(name) ? next.delete(name) : next.add(name);
                  return next;
                })
              }
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
